import axios, { AxiosResponse } from 'axios';
import { RSAOAEPEncryption, PublicKeyResponse } from '../../utils/rsaEncryption';
import apiClient, { API_BASE_URL } from '../api/apiClient';

// API 응답 타입 정의
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface LoginResponse {
  code: number;
  status: string;
  message: string;
  data: {
    access_token: string;
  };
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  createdAt?: string;
}

// 요청/응답 인터셉터는 공통 클라이언트(apiClient.ts)에서만 설정합니다.

// 전역 RSA-OAEP 인스턴스
const rsaEncryption = new RSAOAEPEncryption();

// 공개키 조회 API
export const getPublicKey = async (): Promise<string> => {
  try {
    console.log('공개키 요청:', `${API_BASE_URL}/api/auth/public-key`);
    
    const response: AxiosResponse<PublicKeyResponse> = await apiClient.get('/api/auth/public-key');
    
    console.log('공개키 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });
    
    let publicKey = '';
    
    // data에서 공개키 확인
    if (response.data.data) {
      publicKey = response.data.data;
    } 
    // data가 null이면 message에서 [] 안의 공개키 추출
    else if (response.data.message) {
      const match = response.data.message.match(/\[(.*?)\]/);
      if (match && match[1]) {
        publicKey = match[1];
        console.log('message에서 공개키 추출 성공');
      }
    }
    
    if (!publicKey) {
      throw new Error('공개키를 찾을 수 없습니다.');
    }
    
    console.log({publicKey})
    console.log('공개키 추출 완료:', publicKey.substring(0, 50) + '...');
    return publicKey;
  } catch (error: any) {
    console.error('공개키 조회 실패:', error);
    throw new Error('공개키 조회에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
  }
};

// 비밀번호 암호화
export const encryptPassword = async (password: string): Promise<string> => {
  try {
    // 공개키 가져오기
    const publicKeyData = await getPublicKey();
    
    // 공개키 설정 (Web Crypto API용)
    await rsaEncryption.setPublicKey(publicKeyData);
    
    // 비밀번호 RSA-OAEP 암호화
    const encryptedPassword = await rsaEncryption.encryptData(password);
    
    console.log('RSA-OAEP 비밀번호 암호화 성공');
    return encryptedPassword;
  } catch (error: any) {
    console.error('비밀번호 암호화 실패:', error);
    throw error;
  }
};
// 응답 인터셉터도 공통 클라이언트(apiClient.ts)에서 처리합니다.

// 로그인 API
export const loginApi = async (loginData: LoginRequest): Promise<LoginResponse> => {
  try {
    // 비밀번호 암호화
    const encryptedPassword = await encryptPassword(loginData.password);
    
    console.log('로그인 요청 시작:', { 
      url: `${API_BASE_URL}/api/auth/sign-in`,
      email: loginData.email,
      rememberMe: loginData.rememberMe,
      password: encryptedPassword,
    });

    // 암호화된 비밀번호로 로그인 데이터 구성
    const encryptedLoginData = {
      ...loginData,
      password: encryptedPassword
    };
    
    console.log('암호화된 로그인 데이터:', {
      email: encryptedLoginData.email,
      rememberMe: encryptedLoginData.rememberMe,
      passwordLength: encryptedLoginData.password.length,
      passwordPreview: encryptedLoginData.password
    });
    
    console.log('요청 헤더 및 설정:', {
      baseURL: API_BASE_URL,
      headers: apiClient.defaults.headers,
      withCredentials: apiClient.defaults.withCredentials
    });
    
    // 로컬에서는 프록시 경유로 쿠키를 localhost에 저장, 운영은 원격으로 직접 호출
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    const loginUrl = isLocal ? '/api/auth/sign-in' : `${API_BASE_URL}/api/auth/sign-in`;
    const response: AxiosResponse<LoginResponse> = await axios.post(
      loginUrl,
      encryptedLoginData,
      { withCredentials: true }
    );
    
    console.log('로그인 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      hasToken: !!response.data.data.access_token
    });
    
    // 토큰을 로컬 스토리지에 저장
    if (response.data.data.access_token) {
      localStorage.setItem('access_token', response.data.data.access_token);
    }
    
    return response.data;
  } catch (error: any) {
    console.error('로그인 API 에러:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers
    });
    
    // 에러 메시지 처리
    let errorMessage = '로그인에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
    } else if (error.response.status === 429) {
      errorMessage = '너무 많은 로그인 시도입니다. 잠시 후 다시 시도해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 사용자 프로필 조회 API >> 없는데 ?? 
// export const fetchUserProfile = async (): Promise<User> => {
//   try {
//     const response: AxiosResponse<{data: User}> = await apiClient.get('/api/auth/profile');
//     return response.data.data;
//   } catch (error: any) {
//     const errorMessage = error.response?.data?.message || error.message || '프로필 조회에 실패했습니다.';
//     throw new Error(errorMessage);
//   }
// };

// 로그아웃 API
export const logoutApi = async (): Promise<void> => {
  try {
    console.log('로그아웃 요청 시작:', {
      url: `${API_BASE_URL}/api/auth/sign-out`
    });
    
    // 로컬에서는 프록시 경유, 운영은 원격으로 직접 호출
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    const logoutUrl = isLocal ? '/api/auth/sign-out' : `${API_BASE_URL}/api/auth/sign-out`;
    const response = await axios.post(logoutUrl, {}, { withCredentials: true });
    
    console.log('로그아웃 응답:', {
      response
    });
  } catch (error: any) {
    console.error('로그아웃 API 호출 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    
    // 로그아웃은 로컬에서 토큰만 제거해도 됨
    console.warn('로그아웃 API 호출 실패, 로컬 토큰만 제거합니다.');
  } finally {
    // 로컬 스토리지에서 토큰 제거
    localStorage.removeItem('access_token');
    
    console.log('액세스 토큰이 로컬 스토리지에서 제거되었습니다.');
  }
};

// 토큰 갱신 응답 타입 정의
export interface RefreshTokenResponse {
  code: number;
  status: string;
  message: string;
  data: {
    access_token: string;
    person: {
      id: number;
      name: string;
      birth: string;
      phone: string;
      email: string;
      identity: 'STUDENT' | 'DOCTOR' | 'PROFESSOR' | 'OTHER';
      role: string;
      introduction: string;
      image_url: string;
      id_card_file_url: string;
      university: string;
      medicine: string;
      license: string;
      approved: boolean;
    };
  };
}

// 토큰 갱신 API (interceptor에서 사용하기 위해 별도 axios 인스턴스 사용)
export const refreshToken = async (): Promise<RefreshTokenResponse> => {
  try {
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    
    // 로컬 환경에서는 리프레시 토큰 갱신 API를 호출하지 않음
    if (isLocal) {
      console.log('로컬 환경에서는 리프레시 토큰 갱신을 건너뜁니다.');
      throw new Error('로컬 환경에서는 리프레시 토큰 갱신이 비활성화되어 있습니다.');
    }
    
    const url = `${API_BASE_URL}/api/auth/refresh`;
    console.log('토큰 갱신 요청:', { url });

    // HttpOnly 쿠키는 JS로 읽을 수 없으므로 단순히 withCredentials로 전송
    const refreshResponse: AxiosResponse<RefreshTokenResponse> = await axios.post(
      url,
      {},
      {
        withCredentials: true,
      }
    );

    console.log('토큰 갱신 응답:', {
      status: refreshResponse.status,
      code: refreshResponse.data.code,
      message: refreshResponse.data.message,
      hasNewToken: !!refreshResponse.data.data.access_token
    });

    // 새로운 액세스 토큰을 로컬 스토리지에 저장
    if (refreshResponse.data.data.access_token) {
      localStorage.setItem('access_token', refreshResponse.data.data.access_token);
      console.log('새로운 액세스 토큰이 저장되었습니다.');
    }

    return refreshResponse.data;
  } catch (error: any) {
    console.error('토큰 갱신 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    // 토큰 갱신 실패 시 로컬 스토리지에서 토큰 제거
    localStorage.removeItem('access_token');

    let errorMessage = '토큰 갱신에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '리프레시 토큰이 만료되었습니다. 다시 로그인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 토큰 유효성 검사
export const validateToken = async (): Promise<boolean> => {
  try {
    await apiClient.get('/api/auth/validate');
    return true;
  } catch (error) {
    return false;
  }
};

// 비밀번호 찾기 관련 타입 정의
export interface SendEmailRequest {
  email: string;
}

export interface SendEmailResponse {
  code: number;
  status: string;
  message: string;
  data: string;
}

export interface VerifyCodeRequest {
  email: string;
  authCode: string;
}

export interface VerifyCodeResponse {
  code: number;
  status: string;
  message: string;
  data: boolean;
}

export interface ResetPasswordRequest {
  email: string;
  new_password: string;
}

export interface ResetPasswordResponse {
  code: number;
  status: string;
  message: string;
  data: {};
}

// 비밀번호 찾기 - 이메일 전송 API
export const sendPasswordResetEmail = async (email: string): Promise<SendEmailResponse> => {
  try {
    console.log('비밀번호 찾기 이메일 전송 요청:', {
      url: `${API_BASE_URL}/api/auth/find-password/send-email`,
      email
    });

    const response: AxiosResponse<SendEmailResponse> = await apiClient.post('/api/auth/find-password/send-email', {
      email
    });

    console.log('비밀번호 찾기 이메일 전송 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });

    return response.data;
  } catch (error: any) {
    console.error('비밀번호 찾기 이메일 전송 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    let errorMessage = '이메일 전송에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '등록되지 않은 이메일입니다.';
    } else if (error.response.status === 404) {
      errorMessage = '등록되지 않은 이메일입니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 비밀번호 찾기 - 인증번호 검증 API
export const verifyResetCode = async (email: string, authCode: string): Promise<VerifyCodeResponse> => {
  try {
    console.log('비밀번호 찾기 인증번호 검증 요청:', {
      url: `${API_BASE_URL}/api/auth/find-password/verify-code`,
      email,
      authCode
    });

    const response: AxiosResponse<VerifyCodeResponse> = await apiClient.post('/api/auth/find-password/verify-code', {
      email,
      authCode
    });

    console.log('비밀번호 찾기 인증번호 검증 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });

    return response.data;
  } catch (error: any) {
    console.error('비밀번호 찾기 인증번호 검증 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    let errorMessage = '인증번호 검증에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 인증번호입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증번호가 일치하지 않습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 비밀번호 재설정 API
export const resetPassword = async (email: string, newPassword: string): Promise<ResetPasswordResponse> => {
  try {
    // 비밀번호 암호화
    const encryptedPassword = await encryptPassword(newPassword);
    
    console.log('비밀번호 재설정 요청:', {
      url: `${API_BASE_URL}/api/auth/reset-password`,
      email,
      passwordLength: encryptedPassword.length
    });

    const response: AxiosResponse<ResetPasswordResponse> = await apiClient.put('/api/auth/reset-password', {
      email,
      new_password: encryptedPassword
    });

    console.log('비밀번호 재설정 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });

    return response.data;
  } catch (error: any) {
    console.error('비밀번호 재설정 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    let errorMessage = '비밀번호 재설정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 만료되었습니다. 다시 시도해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 사용자 정보 관련 타입 정의
export interface UserInfo {
  id: number;
  name: string;
  birth: string;
  phone: string;
  email: string;
  identity: 'STUDENT' | 'DOCTOR' | 'PROFESSOR' | 'OTHER';
  role: string;
  introduction: string;
  image_url: string;
  id_card_file_url: string;
  university: string;
  medicine: string;
  license: string;
}

export interface UserInfoResponse {
  code: number;
  status: string;
  message: string;
  data: UserInfo;
}

// 사용자 정보 조회 API
export const getUserInfo = async (): Promise<UserInfo> => {
  try {
    console.log('사용자 정보 조회 요청:', {
      url: `${API_BASE_URL}/api/persons/info`
    });

    const response: AxiosResponse<UserInfoResponse> = await apiClient.get('/api/persons/info');

    console.log('사용자 정보 조회 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });

    return response.data.data;
  } catch (error: any) {
    console.error('사용자 정보 조회 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    let errorMessage = '사용자 정보 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 404) {
      errorMessage = '사용자 정보를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 프로필 이미지 업로드 API
export const uploadProfileImage = async (imageFile: File): Promise<UserInfo> => {
  try {
    console.log('uploadProfileImage 함수 시작');
    
    const formData = new FormData();
    formData.append('image', imageFile);

    console.log('FormData 생성 완료:', {
      url: `${API_BASE_URL}/api/persons/profile-image`,
      fileName: imageFile.name,
      fileSize: imageFile.size,
      fileType: imageFile.type
    });

    // Authorization 헤더 확인
    const token = localStorage.getItem('access_token');
    console.log('토큰 확인:', token ? '토큰 존재' : '토큰 없음');

    const response: AxiosResponse<UserInfoResponse> = await apiClient.patch('/api/persons/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('프로필 이미지 업로드 응답:', {
      status: response.status,
      statusText: response.statusText,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });

    if (response.data.code !== 200) {
      throw new Error(`서버 에러: ${response.data.message}`);
    }

    return response.data.data;
  } catch (error: any) {
    console.error('프로필 이미지 업로드 실패 - 상세 정보:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    });

    let errorMessage = '프로필 이미지 업로드에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 이미지 파일입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 405) {
      errorMessage = '서버에서 지원하지 않는 요청 방식입니다.';
    } else if (error.response.status === 413) {
      errorMessage = '이미지 파일이 너무 큽니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 프로필 이미지 삭제 API
export const deleteProfileImage = async (): Promise<void> => {
  try {
    console.log('프로필 이미지 삭제 요청:', {
      url: `${API_BASE_URL}/api/persons/profile-image`
    });

    const response = await apiClient.delete('/api/persons/profile-image');

    console.log('프로필 이미지 삭제 응답:', {
      status: response.status,
      data: response.data
    });
  } catch (error: any) {
    console.error('프로필 이미지 삭제 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    let errorMessage = '프로필 이미지 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 기본정보 수정 API
export const updateBasicInfo = async (data: {
  name: string;
  birth: string;
  phone: string;
}): Promise<UserInfo> => {
  try {
    console.log('기본정보 수정 요청:', {
      url: `${API_BASE_URL}/api/me/private-info`,
      data
    });

    const response: AxiosResponse<UserInfoResponse> = await apiClient.put('/api/me/private-info', data);

    console.log('기본정보 수정 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });

    if (response.data.code !== 200) {
      throw new Error(`서버 에러: ${response.data.message}`);
    }

    return response.data.data;
  } catch (error: any) {
    console.error('기본정보 수정 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    let errorMessage = '기본정보 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 입력 정보입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 토큰 관리 유틸리티 함수들

// 액세스 토큰 가져오기
export const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};

// 액세스 토큰 저장
export const setAccessToken = (token: string): void => {
  localStorage.setItem('access_token', token);
};

// 토큰 제거
export const removeTokens = (): void => {
  localStorage.removeItem('access_token');
};

// 토큰이 있는지 확인
export const hasToken = (): boolean => {
  return !!localStorage.getItem('access_token');
};

// 앱 초기화 시 토큰 갱신 시도
export const initializeAuth = async (): Promise<boolean> => {
  try {
    console.log('initializeAuth 호출@#@#');
    
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    
    // 로컬 환경에서는 기존 토큰이 있으면 그대로 사용
    if (isLocal) {
      console.log('로컬 환경: 기존 토큰 상태 확인만 수행');
      return hasToken();
    }
    
    // 액세스 토큰이 없으면 리프레시 토큰으로 새 토큰 발급 시도
    if (!hasToken()) {
      console.log('액세스 토큰이 없음, 리프레시 토큰으로 갱신 시도...');
      await refreshToken();
      return true;
    }
    
    // 액세스 토큰이 있으면 유효성 검사
    const isValid = await validateToken();
    if (!isValid) {
      console.log('액세스 토큰이 만료됨, 리프레시 토큰으로 갱신 시도...');
      await refreshToken();
      return true;
    }
    
    console.log('기존 액세스 토큰이 유효함');
    return true;
  } catch (error) {
    console.error('인증 초기화 실패:', error);
    removeTokens();
    return false;
  }
};

// 회원 자격 인증 API 관련 타입 정의
export interface IdentityVerificationRequest {
  identity: 'STUDENT' | 'DOCTOR';
  department: string;
  idCardFile?: File;
  licenseNumber?: string;
}

export interface IdentityVerificationResponse {
  code: number;
  status: string;
  message: string;
  data: {
    identity: 'STUDENT' | 'DOCTOR';
    department: string;
    id_card_file?: string;
    license_number?: string;
  };
}

// 회원 자격 인증 API
export const verifyIdentity = async (data: IdentityVerificationRequest): Promise<IdentityVerificationResponse> => {
  try {
    console.log('회원 자격 인증 요청:', {
      url: `${API_BASE_URL}/api/persons/identity`,
      identity: data.identity,
      department: data.department,
      hasFile: !!data.idCardFile,
      licenseNumber: data.licenseNumber ? '****' + data.licenseNumber.slice(-4) : undefined
    });

    const formData = new FormData();
    formData.append('identity', data.identity);
    formData.append('department', data.department);
    
    // 의대생인 경우 학생증/재학증명서 파일 필수
    if (data.identity === 'STUDENT' && data.idCardFile) {
      formData.append('idCardFile', data.idCardFile);
    }
    
    // 의사인 경우 면허번호 필수
    if (data.identity === 'DOCTOR' && data.licenseNumber) {
      formData.append('licenseNumber', data.licenseNumber);
    }

    const response: AxiosResponse<IdentityVerificationResponse> = await apiClient.put('/api/persons/identity', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('회원 자격 인증 응답:', {
      status: response.status,
      code: response.data.code,
      message: response.data.message,
      data: response.data.data
    });

    if (response.data.code !== 200) {
      throw new Error(`서버 에러: ${response.data.message}`);
    }

    return response.data;
  } catch (error: any) {
    console.error('회원 자격 인증 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    });

    let errorMessage = '회원 자격 인증에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '입력 정보를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 413) {
      errorMessage = '업로드 파일이 너무 큽니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

export default apiClient;